import type { FC, ReactNode } from 'react';

import { Icon } from '../../components/icon';
import { IconButton } from '../../components/icon-button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/popover';
import { cn } from '../../lib/utils';

export interface RiskScoreBreakdownControlTypeRow {
  /** Already-resolved label (e.g. "Preventive" / "Unclassified"). */
  label: string;
  /** Pluralised count copy, e.g. "2 controls". */
  count: string;
  /** Avg-effectiveness display, e.g. "3 avg". */
  value: string;
}

export interface RiskScoreBreakdownDimensionRow {
  /** Dimension label (e.g. "Likelihood"). */
  label: string;
  /** Aggregate avg display (e.g. "avg 2.8" or "avg —"). */
  aggregate: string;
  /** Reduction percent + band display, e.g. "55% (band 2–3)". */
  reduction: string;
}

export interface RiskScoreBreakdownCalculationSection {
  heading: string;
  /** Pre-formatted formula lines, rendered with `font-mono`. */
  lines: string[];
}

export interface RiskScoreBreakdownDetails {
  controlTypesHeading: string;
  controlTypes: RiskScoreBreakdownControlTypeRow[];
  dimensionsHeading: string;
  dimensions: RiskScoreBreakdownDimensionRow[];
  calculation?: {
    summaryLabel: string;
    sections: RiskScoreBreakdownCalculationSection[];
  };
}

export interface RiskScoreBreakdownPopoverProps {
  /** aria-label on the trigger icon button. */
  triggerLabel: string;
  /** Narrative paragraphs rendered above the details section. */
  paragraphs: string[];
  /**
   * Optional structured details. When omitted (e.g. for an `incomplete`
   * breakdown) only the paragraphs render.
   */
  details?: RiskScoreBreakdownDetails;
  /**
   * Optional override for the trigger icon. Defaults to `info-circle`.
   */
  triggerIcon?: ReactNode;
}

const RowGroup: FC<{ label: string; count: string; value: string }> = ({
  label,
  count,
  value,
}) => (
  <>
    <span className={cn('text-sm')}>{label}</span>
    <span className={cn('text-sm text-muted-foreground')}>{count}</span>
    <span className={cn('text-sm tabular-nums')}>{value}</span>
  </>
);

const ControlTypeRows: FC<{
  heading: string;
  rows: RiskScoreBreakdownControlTypeRow[];
}> = ({ heading, rows }) => {
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-1')}>
      <p
        className={cn(
          'm-0 text-sm font-semibold uppercase text-muted-foreground'
        )}
      >
        {heading}
      </p>
      <div className={cn('grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0.5')}>
        {rows.map((row) => (
          <RowGroup
            key={row.label}
            label={row.label}
            count={row.count}
            value={row.value}
          />
        ))}
      </div>
    </div>
  );
};

const DimensionRows: FC<{
  heading: string;
  rows: RiskScoreBreakdownDimensionRow[];
}> = ({ heading, rows }) => (
  <div className={cn('flex flex-col gap-1')}>
    <p
      className={cn(
        'm-0 text-xs font-semibold uppercase text-muted-foreground'
      )}
    >
      {heading}
    </p>
    <div className={cn('grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0.5')}>
      {rows.map((row) => (
        <RowGroup
          key={row.label}
          label={row.label}
          count={row.aggregate}
          value={row.reduction}
        />
      ))}
    </div>
  </div>
);

const CalculationDisclosure: FC<{
  summaryLabel: string;
  sections: RiskScoreBreakdownCalculationSection[];
}> = ({ summaryLabel, sections }) => (
  <details className={cn('group/calc')} data-testid={'risk-score-breakdown-calc'}>
    <summary
      className={cn(
        'm-0 cursor-pointer list-none text-sm font-semibold uppercase text-muted-foreground hover:text-foreground'
      )}
    >
      <span
        className={cn(
          'inline-block transition-transform group-open/calc:rotate-90'
        )}
      >
        {'›'}
      </span>{' '}
      {summaryLabel}
    </summary>
    <div className={cn('mt-2 flex flex-col gap-3')}>
      {sections.map((section) => (
        <div key={section.heading} className={cn('flex flex-col gap-0.5')}>
          <p className={cn('m-0 text-sm font-semibold')}>{section.heading}</p>
          {section.lines.map((line, index) => (
            <p
              key={index}
              className={cn(
                'm-0 text-sm font-mono tabular-nums leading-relaxed whitespace-pre-wrap'
              )}
            >
              {line}
            </p>
          ))}
        </div>
      ))}
    </div>
  </details>
);

const Details: FC<{ details: RiskScoreBreakdownDetails }> = ({ details }) => (
  <div
    className={cn('flex flex-col gap-3 pt-2 border-t border-neutral-border')}
    data-testid={'risk-score-breakdown-details'}
  >
    <ControlTypeRows
      heading={details.controlTypesHeading}
      rows={details.controlTypes}
    />
    <DimensionRows
      heading={details.dimensionsHeading}
      rows={details.dimensions}
    />
    {details.calculation && (
      <CalculationDisclosure
        summaryLabel={details.calculation.summaryLabel}
        sections={details.calculation.sections}
      />
    )}
  </div>
);

/**
 * The popover body — paragraphs + optional details — rendered without a
 * `<Popover>` wrapper. Exposed so consumers can host the same content in
 * a different surface (e.g. a side panel) and so the layout branches are
 * testable without exercising a portalled popover.
 */
const RiskScoreBreakdownPopoverContent: FC<{
  paragraphs: string[];
  details?: RiskScoreBreakdownDetails;
}> = ({ paragraphs, details }) => (
  <div className={cn('flex flex-col gap-3 text-base')}>
    <div className={cn('flex flex-col gap-2')}>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className={cn('m-0 leading-snug')}>
          {paragraph}
        </p>
      ))}
    </div>
    {details && <Details details={details} />}
  </div>
);

const RiskScoreBreakdownPopover: FC<RiskScoreBreakdownPopoverProps> = ({
  triggerLabel,
  paragraphs,
  details,
  triggerIcon,
}) => (
  <Popover>
    <PopoverTrigger
      render={(triggerProps) => (
        <IconButton
          {...triggerProps}
          aria-label={triggerLabel}
          data-testid={'risk-score-breakdown-trigger'}
        >
          {triggerIcon ?? <Icon name={'info-circle'} size={'sm'} />}
        </IconButton>
      )}
    />
    <PopoverContent
      className={cn('max-w-md p-4')}
      data-testid={'risk-score-breakdown-content'}
    >
      <RiskScoreBreakdownPopoverContent
        paragraphs={paragraphs}
        details={details}
      />
    </PopoverContent>
  </Popover>
);

export { RiskScoreBreakdownPopover, RiskScoreBreakdownPopoverContent };
